unit U_ApiClient;

{ -------------------------------------------------------------------------------
  Small REST client wrapper for TMS WEB Core.

  Goals:
  - Centralize request preparation (credentials + JSON headers).
  - Centralize error handling (network errors + HTTP 401 behavior).
  - Keep the form code focused on UI and payload parsing.
  ------------------------------------------------------------------------------- }
interface

uses
  System.SysUtils, System.Classes,
  WEBLib.WebTools, WEBLib.REST,
  JS, Web;

type
  // Callbacks receive the raw response body and the underlying XHR object.
  TApiOkProc = reference to procedure(const Body: string;
    XHR: TJSXMLHttpRequest);
  TApiFailProc = reference to procedure(const Body: string;
    XHR: TJSXMLHttpRequest);

  // Lightweight client. One instance can be reused for the whole application.

  TApiClient = class
  private
    FOwner: TComponent;
    FBaseUrl: string;

    FOnUnauthorized: TProc;
    FOnNetworkError: TProc<string>;

    // Configure common request headers and browser options.

    procedure Prepare(req: THttpRequest;
      const AcceptJson, ContentJson: Boolean);
    // Execute the request and route the result to OnOk / OnFail.
    // Also triggers optional global handlers for unauthorized and network errors.
    procedure Exec(req: THttpRequest; const OnOk: TApiOkProc;
      const OnFail: TApiFailProc);

  public
    constructor Create(AOwner: TComponent; const ABaseUrl: string);

    property BaseUrl: string read FBaseUrl write FBaseUrl;
    property OnUnauthorized: TProc read FOnUnauthorized write FOnUnauthorized;
    property OnNetworkError: TProc<string> read FOnNetworkError
      write FOnNetworkError;

    procedure Get(const Path: string; const OnOk: TApiOkProc;
      const OnFail: TApiFailProc = nil);
    procedure PostJSON(const Path, JsonBody: string; const OnOk: TApiOkProc;
      const OnFail: TApiFailProc = nil);
    procedure PutJSON(const Path, JsonBody: string; const OnOk: TApiOkProc;
      const OnFail: TApiFailProc = nil);
    procedure Delete(const Path: string; const OnOk: TApiOkProc;
      const OnFail: TApiFailProc = nil);
  end;

implementation

{ TApiClient }

constructor TApiClient.Create(AOwner: TComponent; const ABaseUrl: string);
begin
  inherited Create;
  FOwner := AOwner;
  FBaseUrl := ABaseUrl;
end;

procedure TApiClient.Prepare(req: THttpRequest;
  const AcceptJson, ContentJson: Boolean);
begin
  req.WithCredentials := True;

  if AcceptJson then
    req.Headers.Add('Accept=application/json');
  if ContentJson then
    req.Headers.Add('Content-Type=application/json');
end;

procedure TApiClient.Exec(req: THttpRequest; const OnOk: TApiOkProc;
  const OnFail: TApiFailProc);
begin
  // We rely only on Execute callback. It receives the XHR even on failures.
  req.Execute(
    procedure(Body: string; XHR: TJSXMLHttpRequest)
    begin
      try
        // Network error: some browsers report status 0
        if (XHR = nil) or (XHR.status = 0) then
        begin
          if Assigned(FOnNetworkError) then
            FOnNetworkError('Network error (status 0)');
          if Assigned(OnFail) then
            OnFail(Body, XHR);
          Exit;
        end;

        // Central 401 handling
        // NOTE: during /login, a 401 means "bad credentials" and should NOT be treated
        // as an expired session (otherwise the UI gets reset while user is logging in).
        if XHR.status = 401 then
        begin
          // Always forward the error to the call-site so it can display a proper message.
          if Assigned(OnFail) then
            OnFail(Body, XHR);

          // Only trigger the global unauthorized handler when we're not calling /login.
          if Assigned(FOnUnauthorized) and (Pos('/login', LowerCase(req.URL)
            ) = 0) then
            FOnUnauthorized;
          Exit;
        end;

        if (XHR.status >= 200) and (XHR.status < 300) then
        begin
          if Assigned(OnOk) then
            OnOk(Body, XHR);
        end
        else
        begin
          if Assigned(OnFail) then
            OnFail(Body, XHR);
        end;
      finally
        req.Free; // avoid leaks
      end;
    end);
end;

procedure TApiClient.Get(const Path: string; const OnOk: TApiOkProc;
const OnFail: TApiFailProc);
var
  req: THttpRequest;
begin
  req := THttpRequest.Create(FOwner);
  req.Command := THTTPCommand.HttpGet;
  req.URL := FBaseUrl + Path;
  Prepare(req, True, False);
  Exec(req, OnOk, OnFail);
end;

procedure TApiClient.PostJSON(const Path, JsonBody: string;
const OnOk: TApiOkProc; const OnFail: TApiFailProc);
var
  req: THttpRequest;
begin
  req := THttpRequest.Create(FOwner);
  req.Command := THTTPCommand.httpPOST;
  req.URL := FBaseUrl + Path;
  Prepare(req, True, True);
  req.PostData := JsonBody;
  Exec(req, OnOk, OnFail);
end;

procedure TApiClient.PutJSON(const Path, JsonBody: string;
const OnOk: TApiOkProc; const OnFail: TApiFailProc);
var
  req: THttpRequest;
begin
  req := THttpRequest.Create(FOwner);
  req.Command := THTTPCommand.httpPUT;
  req.URL := FBaseUrl + Path;
  Prepare(req, True, True);
  req.PostData := JsonBody;
  Exec(req, OnOk, OnFail);
end;

procedure TApiClient.Delete(const Path: string; const OnOk: TApiOkProc;
const OnFail: TApiFailProc);
var
  req: THttpRequest;
begin
  req := THttpRequest.Create(FOwner);
  req.Command := THTTPCommand.httpDELETE;
  req.URL := FBaseUrl + Path;
  Prepare(req, True, False);
  Exec(req, OnOk, OnFail);
end;

end.
